import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from '@tanstack/react-router';
import {
  ArrowDown,
  Check,
  ChevronRight,
  ClipboardCheck,
  Clock,
  Eye,
  Info,
  TriangleAlert,
  Users,
  X,
} from 'lucide-react';
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type {
  ChecklistItem,
  ChecklistResult,
  ChecklistRun,
  ChecklistRunList,
} from '@up2d8/contracts';
import { CHECKLIST_MAX_PHOTOS } from '@up2d8/contracts';
import { problemDetail } from '@up2d8/api-client';
import { Avatar, Badge, Card, CardTitle, cn } from '@up2d8/ui';
import { enqueue, flushOutbox, pendingChecklistEntries } from '../../offline/outbox';
import { queriesAfterSending, stillShownAsLocal } from '../../offline/outbox-rules';
import { HANDLES_OFFLINE } from '../../offline/network-mode';
import { useOffline } from '../../offline/use-offline';
import { absentOrThrow } from '../../shared/home-card';
import { useSession } from '../../shared/session';
import { useTenantTheme } from '../../shared/use-tenant-theme';
import { renderAppTabLink, useAppTabs } from './app-tabs';
import { readOnlyReason, readyToFinish } from './checklist-run-state';
import { DeskShell } from './desk/desk-shell';
import {
  Crumbs,
  EmptyCard,
  FieldError,
  FloorBadge,
  OfflineStrip,
  PhotoPicker,
  SkeletonCards,
  handOverToast,
  takeHandedOverToast,
  useClock,
  useFloorToast,
  useWhen,
  type Photo,
} from './floor/floor-kit';
import { MessagesIcon } from './messages-icon';
import { NotificationBell } from './notification-bell';

/**
 * Checklists voor de medewerker (Stap 38), in de vloermodus (VLR-2,
 * `docs/design/UP2D8-checklists-replica-spec.md`).
 *
 * Twee adressen: de lijst, en één uitvoering (`/app/checklists/$runId`). Het tweede adres is er
 * voor "Mijn taken", dat er al naartoe linkte, en voor de terugknop van de telefoon: wie terugveegt
 * uit een checklist, hoort op de lijst te landen en niet op Home.
 *
 * **Afvinken werkt zonder netwerk** (acceptatiecriterium 3). Elke tik gaat eerst naar de wachtrij
 * van Stap 12 en dan naar de server; lukt dat niet, dan blijft hij staan met het tijdstip waarop de
 * medewerker het werkelijk deed (ADR-0047). Een item dat zo wacht, staat op het scherm als
 * afgevinkt, met een grijze stip en "Wacht op verzending" — zonder dat zou een operator die offline
 * afvinkt niets zien gebeuren, en dan vinkt hij nog een keer.
 */

const MY_RUNS = ['me', 'checklists'] as const;

function useMyRuns() {
  const { api } = useSession();
  return useQuery({
    queryKey: MY_RUNS,
    queryFn: async (): Promise<ChecklistRun[]> => {
      const { data, error } = await api.GET('/api/v1/me/checklists', {});
      if (error || !data) throw error ?? new Error('Ophalen mislukt');
      return (data as ChecklistRunList).items;
    },
  });
}

/** Wat er offline afgevinkt werd en nog wacht, per `runId:itemId`. */
function usePending() {
  return useQuery({
    queryKey: [...MY_RUNS, 'pending'],
    queryFn: async () => pendingChecklistEntries(),
    // IndexedDB, geen netwerk: ook offline lezen, anders verschijnt het klokje nooit.
    ...HANDLES_OFFLINE,
    // Vaak genoeg om het klokje te laten verdwijnen zodra de wachtrij leegloopt, zelden genoeg om
    // niets te kosten op een telefoon.
    refetchInterval: 3000,
  });
}

/** Te laat · Vandaag · Later (spec §2). Een te late checklist is te laat, ook als hij van vandaag is. */
function groupOf(run: ChecklistRun, now: Date): 'late' | 'today' | 'later' {
  const due = new Date(run.dueAt);
  if (run.status === 'overdue' || due.getTime() < now.getTime()) return 'late';
  return due.toDateString() === now.toDateString() ? 'today' : 'later';
}

/** De doelgroepen van een lijst, zonder dubbels: "Ploeg B · Lijn 3". */
function audienceOf(runs: ChecklistRun[]): string {
  return [...new Set(runs.map((run) => run.audienceDescription).filter(Boolean))].join(', ');
}

function useShellProps() {
  const tabs = useAppTabs();
  return {
    tabs,
    // Home blijft actief in de onderbalk: dit scherm hoort bij de stapel onder Home. In de zijbalk
    // licht Mijn taken op, want daar komen checklists vandaan (spec §1).
    activeKey: 'home',
    deskActiveKey: 'checklists',
    renderLink: renderAppTabLink,
    messages: <MessagesIcon />,
    bell: <NotificationBell />,
  };
}

// --- De lijst ---------------------------------------------------------------------------

export function AppChecklistsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const shell = useShellProps();
  const runs = useMyRuns();
  const when = useWhen();
  const { show, element: toast } = useFloorToast();

  useTenantTheme();

  // De melding van het invulscherm ("De checklist is afgerond.") verschijnt hier.
  useEffect(() => {
    const message = takeHandedOverToast();
    if (message) show(message);
  }, [show]);

  const now = new Date();
  const items = runs.data ?? [];
  const groups = {
    late: items.filter((run) => groupOf(run, now) === 'late'),
    today: items.filter((run) => groupOf(run, now) === 'today'),
    later: items.filter((run) => groupOf(run, now) === 'later'),
  };
  const audience = audienceOf(items);
  const sub = [
    audience,
    items.length > 0 ? t('appChecklists.openCount', { count: items.length }) : null,
  ]
    .filter(Boolean)
    .join(' · ');
  const deviations = items.reduce((sum, run) => sum + run.deviations, 0);

  return (
    <DeskShell
      {...shell}
      subBar={{
        title: t('appChecklists.title'),
        onBack: () => void navigate({ to: '/app' }),
        backLabel: t('floor.back'),
      }}
    >
      <div className="fm cf-grid" data-testid="checklists-page">
        <div className="cf-col">
          <div>
            <Crumbs
              items={[
                { label: t('myTasks.title'), to: '/app/taken' },
                { label: t('appChecklists.title') },
              ]}
            />
            <h1 className="cf-title cf-desk-title">{t('appChecklists.title')}</h1>
            {sub ? (
              <>
                <div className="cf-sub cf-desk-only">{sub}</div>
                <div className="fm-meta cf-phone-only">{sub}</div>
              </>
            ) : null}
          </div>

          {runs.isPending ? (
            <SkeletonCards />
          ) : runs.isError ? (
            <EmptyCard
              tone="danger"
              icon={<TriangleAlert aria-hidden width={32} height={32} />}
              title={t('errors.generic')}
              text={t('appChecklists.emptyHint')}
            />
          ) : items.length === 0 ? (
            <EmptyCard
              testId="checklists-empty"
              icon={<Check aria-hidden width={34} height={34} strokeWidth={2.5} />}
              title={t('appChecklists.emptyTitle')}
              text={t('appChecklists.emptyHint')}
            />
          ) : (
            <div className="fm-section">
              {(['late', 'today', 'later'] as const).map((key) =>
                groups[key].length === 0 ? null : (
                  <div key={key} className="fm-section" data-testid={`checklist-group-${key}`}>
                    <h2 className={cn('cf-grouplabel', key === 'late' && 'danger')}>
                      {t(
                        key === 'late'
                          ? 'appChecklists.groupLate'
                          : key === 'today'
                            ? 'appChecklists.groupToday'
                            : 'appChecklists.groupLater',
                      )}{' '}
                      <span className="fm-h-count">{groups[key].length}</span>
                    </h2>
                    {groups[key].map((run) => (
                      <RunCard key={run.id} run={run} late={key === 'late'} when={when} />
                    ))}
                  </div>
                ),
              )}
            </div>
          )}
        </div>

        {items.length > 0 ? (
          <aside className="cf-aside cf-desk-only">
            <div className="fm-card">
              <div className="cf-side-title">
                <Users aria-hidden width={20} height={20} />
                {audience
                  ? t('appChecklists.todayFor', { who: audience })
                  : t('appChecklists.todayForYou')}
              </div>
              <div className="fm-stats">
                <div className="fm-stat">
                  <div className="fm-stat-num">{items.length}</div>
                  <div className="fm-stat-label">{t('appChecklists.statOpen')}</div>
                </div>
                <div className="fm-stat">
                  <div className="fm-stat-num" style={{ color: 'var(--fm-danger-text)' }}>
                    {groups.late.length}
                  </div>
                  <div className="fm-stat-label">{t('appChecklists.statLate')}</div>
                </div>
                <div className="fm-stat">
                  <div className="fm-stat-num" style={{ color: 'var(--fm-orange-text)' }}>
                    {deviations}
                  </div>
                  <div className="fm-stat-label">{t('appChecklists.statDeviation')}</div>
                </div>
              </div>
              <div className="fm-hint">{t('appChecklists.teamHint')}</div>
            </div>
          </aside>
        ) : null}
        {toast}
      </div>
    </DeskShell>
  );
}

function RunCard({
  run,
  late,
  when,
}: {
  run: ChecklistRun;
  late: boolean;
  when: (iso: string) => string;
}) {
  const { t } = useTranslation();
  const done = run.results.length;
  const total = run.items.length;

  return (
    <Link
      to="/app/checklists/$runId"
      params={{ runId: run.id }}
      className={cn('fm-card cf-listcard', late && 'late')}
      data-testid="checklist-card"
    >
      <div className={cn('cf-roundicon', late ? 'danger' : 'orange')}>
        <ClipboardCheck aria-hidden width={24} height={24} />
      </div>
      <div className="cf-listcard-info">
        <div className="fm-title">{run.templateName}</div>
        <div
          className={cn('fm-meta', late && 'urgent')}
          style={late ? { color: 'var(--fm-danger-text)', fontWeight: 600 } : undefined}
        >
          <Clock aria-hidden width={16} height={16} />
          {t('appChecklists.dueAt', { when: when(run.dueAt) })}
        </div>
        <div className="cf-progress-line">
          <div className="fm-progress">
            <span style={{ width: `${total === 0 ? 0 : Math.round((done / total) * 100)}%` }} />
          </div>
          {t('appChecklists.remaining', { count: run.remaining })}
        </div>
        {late || run.deviations > 0 ? (
          <div className="cf-badges">
            {late ? (
              <FloorBadge tone="danger" icon={<Clock aria-hidden width={14} height={14} />}>
                {t('appChecklists.overdue')}
              </FloorBadge>
            ) : null}
            {run.deviations > 0 ? (
              <FloorBadge
                tone="attention"
                icon={<TriangleAlert aria-hidden width={14} height={14} />}
              >
                {t('appChecklists.deviationCount', { count: run.deviations })}
              </FloorBadge>
            ) : null}
          </div>
        ) : null}
      </div>
      <ChevronRight aria-hidden width={22} height={22} className="fm-chev" />
    </Link>
  );
}

// --- Eén checklist invullen -------------------------------------------------------------

/** Een item zoals het scherm het toont: van de server, of uit de wachtrij als dat nieuwer is. */
interface ItemState {
  item: ChecklistItem;
  value: string;
  comment: string;
  fileIds: string[];
  deviation: boolean;
  doneById: string | null;
  doneByName: string | null;
  doneAt: string | null;
  done: boolean;
  queued: boolean;
}

/** Dezelfde regel als de server (`checkValue`): "nee" en een getal buiten de norm zijn een afwijking. */
function localDeviation(item: ChecklistItem, value: string): boolean {
  if (item.type === 'check') return value === 'nee';
  if (item.type !== 'number' || value.trim() === '') return false;
  const number = Number(value.replace(',', '.'));
  if (!Number.isFinite(number)) return false;
  return (item.min !== null && number < item.min) || (item.max !== null && number > item.max);
}

export function AppChecklistRunPage() {
  const { t } = useTranslation();
  const { runId } = useParams({ strict: false }) as { runId: string };
  const navigate = useNavigate();
  const shell = useShellProps();
  const session = useSession();
  const queryClient = useQueryClient();
  const { online } = useOffline();
  const when = useWhen();
  const { show, element: toast } = useFloorToast();

  useTenantTheme();

  /*
    Eén uitvoering ophalen, ook als ze niet in mijn lijst staat.

    Wie via een link op een checklist van een andere ploeg landt, ziet hem in alleen-lezen (spec
    §4): `canFill` komt van de server en zegt dat. De sleutel hangt onder die van de lijst, zodat
    één invalidatie allebei ververst.
  */
  const run = useQuery({
    queryKey: [...MY_RUNS, 'run', runId],
    queryFn: async (): Promise<(ChecklistRun & { fetchStartedAt: number }) | null> => {
      // Wanneer gevraagd, niet wanneer ontvangen: een ophaling die vertrok vóór de wachtrij
      // verzonden was, kent het afgevinkte item nog niet (zie `stillShownAsLocal`).
      const fetchStartedAt = Date.now();
      const { data, error, response } = await session.api.GET('/api/v1/me/checklists/{runId}', {
        params: { path: { runId } },
      });
      if (response?.status === 404) return null;
      if (error || !data) throw error ?? new Error('Ophalen mislukt');
      return { ...(data as ChecklistRun), fetchStartedAt };
    },
  });
  const pending = usePending();

  const [current, setCurrent] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const refs = useRef(new Map<string, HTMLElement>());

  const states: ItemState[] = useMemo(() => {
    const data = run.data;
    if (!data) return [];
    return [...data.items]
      .sort((a, b) => a.position - b.position)
      .map((item) => {
        const result: ChecklistResult | undefined = data.results.find(
          (candidate) => candidate.itemId === item.id,
        );
        const local = pending.data?.get(`${data.id}:${item.id}`);
        // Wat op dit toestel afgevinkt is, blijft afgevinkt tot de server het in verse gegevens
        // teruggeeft; anders springt het item na "weer online" even terug naar open.
        const queued =
          local && stillShownAsLocal(local.sentAt, data.fetchStartedAt) ? local : undefined;
        if (queued) {
          return {
            item,
            value: queued.value,
            comment: queued.comment,
            fileIds: result?.fileIds ?? [],
            deviation: localDeviation(item, queued.value),
            doneById: session.profile?.personId ?? null,
            doneByName: session.profile?.displayName ?? null,
            doneAt: queued.occurredAt,
            done: true,
            // Verzonden maar nog niet ververst: afgevinkt, zonder "Wacht op verzending".
            queued: queued.sentAt === null,
          };
        }
        return {
          item,
          value: result?.value ?? '',
          comment: result?.comment ?? '',
          fileIds: result?.fileIds ?? [],
          deviation: result?.deviation ?? false,
          doneById: result?.doneById ?? null,
          doneByName: result?.doneByName ?? null,
          doneAt: result?.doneAt ?? null,
          done: result !== undefined,
          queued: false,
        };
      });
  }, [run.data, pending.data, session.profile]);

  const openStates = states.filter((state) => !state.done);
  const doneCount = states.length - openStates.length;
  const deviations = states.filter((state) => state.done && state.deviation);
  const canFill = run.data?.canFill ?? false;
  // Het item waar je nu bent: wat je koos, anders het eerste open item.
  const currentId =
    current && openStates.some((state) => state.item.id === current)
      ? current
      : (openStates[0]?.item.id ?? null);

  /** "Nog 3 te doen" springt naar het volgende open item: de knop is nooit een dode knop. */
  const jump = () => {
    const at = openStates.findIndex((state) => state.item.id === currentId);
    const next = openStates[(at + 1) % openStates.length] ?? openStates[0];
    if (!next) return;
    setCurrent(next.item.id);
    const target = refs.current.get(next.item.id);
    target?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    target?.querySelector<HTMLElement>('button, input, textarea')?.focus({ preventScroll: true });
  };

  const backToList = () => void navigate({ to: '/app/checklists' });

  const complete = useMutation({
    mutationFn: async () => {
      // Eerst de wachtrij: de server kent een offline afgevinkt item pas als het verstuurd is.
      await flushOutbox(session.api);
      const { error } = await session.api.POST('/api/v1/me/checklists/{runId}/complete', {
        params: { path: { runId } },
      });
      if (error) throw error;
    },
    onSuccess: async () => {
      // Ook "Te doen": de afgeronde checklist hoort daar niet meer bij.
      void queryClient.invalidateQueries({ queryKey: ['open-items'] });
      await queryClient.invalidateQueries({ queryKey: MY_RUNS });
      handOverToast({
        tone: 'ok',
        text:
          deviations.length > 0
            ? t('appChecklists.completedWithDeviations', { count: deviations.length })
            : t('appChecklists.completed'),
      });
      backToList();
    },
    onError: (error) => show({ tone: 'err', text: problemDetail(error) ?? t('errors.generic') }),
  });

  const afterSave = (itemId: string) => {
    setExpanded((now) => {
      const next = new Set(now);
      next.delete(itemId);
      return next;
    });
    const next = openStates.find((state) => state.item.id !== itemId);
    setCurrent(next?.item.id ?? null);
  };

  /*
    Zolang er een verplicht item open staat, is de hoofdknop "Nog 3 te doen" en springt hij naar
    het volgende open item; het getal telt alles wat nog leeg staat, ook optionele items. Afronden
    kan zodra de verplichte items af zijn, zoals op de server (`readyToFinish`).
  */
  const mainButton = !readyToFinish(states) ? (
    <button
      type="button"
      className="fm-btn soft-done"
      aria-label={t('appChecklists.nextOpenLabel', { count: openStates.length })}
      onClick={jump}
      data-testid="checklist-next"
    >
      <ArrowDown aria-hidden width={20} height={20} />
      {t('appChecklists.nextOpen', { count: openStates.length })}
    </button>
  ) : (
    <button
      type="button"
      className="fm-btn"
      disabled={complete.isPending}
      onClick={() => complete.mutate()}
      data-testid="checklist-complete"
    >
      <Check aria-hidden width={20} height={20} strokeWidth={2.5} />
      {t('appChecklists.finish')}
    </button>
  );

  const footer = !run.data ? undefined : canFill ? (
    <div className="fm-footer">
      <div className="fm-footer-row">
        <button
          type="button"
          className="fm-btn outline"
          onClick={backToList}
          data-testid="checklist-close"
        >
          {t('appChecklists.later')}
        </button>
        {mainButton}
      </div>
    </div>
  ) : (
    <div className="fm-footer">
      <button type="button" className="fm-btn outline block" onClick={backToList}>
        {t('appChecklists.backToList')}
      </button>
    </div>
  );

  const name = run.data?.templateName ?? t('appChecklists.checklist');

  return (
    <DeskShell
      {...shell}
      subBar={{
        title: t('appChecklists.checklist'),
        onBack: backToList,
        backLabel: t('floor.back'),
      }}
      mobileFooter={footer}
    >
      <div className="fm cf-grid" data-testid="checklist-form">
        {!online && run.data && canFill ? (
          <div className="w-full" style={{ gridColumn: '1 / -1' }}>
            <div className="cf-phone-only">
              <OfflineStrip>{t('appChecklists.offline')}</OfflineStrip>
            </div>
            <div className="cf-desk-only flex-col">
              <OfflineStrip>{t('appChecklists.offlineDesk')}</OfflineStrip>
            </div>
          </div>
        ) : null}

        <div className="cf-col">
          <div>
            <Crumbs
              items={[
                { label: t('myTasks.title'), to: '/app/taken' },
                { label: t('appChecklists.title'), to: '/app/checklists' },
                { label: name },
              ]}
            />
          </div>

          {run.isPending ? (
            <SkeletonCards count={2} />
          ) : !run.data ? (
            <EmptyCard
              tone="neutral"
              icon={<ClipboardCheck aria-hidden width={32} height={32} />}
              title={t('appChecklists.checklist')}
              text={t('appChecklists.notFound')}
            />
          ) : (
            <>
              <div className="fm-card cl-head">
                <div className="cl-head-top">
                  <div className="cf-roundicon orange">
                    <ClipboardCheck aria-hidden width={24} height={24} />
                  </div>
                  <div className="cf-listcard-info">
                    <h1 className="cf-title cf-desk-title">{name}</h1>
                    <div className="fm-big cf-phone-only">{name}</div>
                    <div className="fm-meta">
                      <Clock aria-hidden width={16} height={16} />
                      {t('appChecklists.dueAt', { when: when(run.data.dueAt) })}
                    </div>
                  </div>
                </div>
                <div className="cf-progress-line">
                  <span className="cl-progress-num" data-testid="checklist-progress">
                    {t('appChecklists.progress', { done: doneCount, total: states.length })}
                  </span>
                  {deviations.length > 0 ? (
                    <FloorBadge
                      tone="attention"
                      icon={<TriangleAlert aria-hidden width={14} height={14} />}
                    >
                      {t('appChecklists.deviationCount', { count: deviations.length })}
                    </FloorBadge>
                  ) : null}
                </div>
                <div
                  className={cn('fm-progress', doneCount === states.length && 'full')}
                  role="progressbar"
                  aria-valuenow={doneCount}
                  aria-valuemin={0}
                  aria-valuemax={states.length}
                >
                  <span
                    style={{
                      width: `${states.length === 0 ? 0 : Math.round((doneCount / states.length) * 100)}%`,
                    }}
                  />
                </div>
              </div>

              {!canFill ? (
                <div className="fm-notice" data-testid="checklist-readonly">
                  <Eye aria-hidden width={20} height={20} />
                  <div>
                    <strong>{t('appChecklists.readOnlyTitle')}</strong>{' '}
                    {readOnlyReason(run.data) === 'completed'
                      ? t('appChecklists.readOnlyCompleted')
                      : run.data.audienceDescription
                        ? t('appChecklists.readOnlyWho', { who: run.data.audienceDescription })
                        : null}
                  </div>
                </div>
              ) : null}

              {states.map((state) => {
                const editing = !state.done || (canFill && expanded.has(state.item.id));
                return (
                  <div
                    key={state.item.id}
                    ref={(node) => {
                      if (node) refs.current.set(state.item.id, node);
                      else refs.current.delete(state.item.id);
                    }}
                  >
                    {editing ? (
                      <ItemEditor
                        runId={runId}
                        state={state}
                        disabled={!canFill}
                        online={online}
                        current={canFill && currentId === state.item.id}
                        onFocus={() => setCurrent(state.item.id)}
                        onSaved={() => afterSave(state.item.id)}
                      />
                    ) : (
                      <DoneRow
                        state={state}
                        expandable={canFill}
                        onOpen={() => setExpanded((now) => new Set(now).add(state.item.id))}
                      />
                    )}
                  </div>
                );
              })}
            </>
          )}
        </div>

        {run.data ? (
          <aside className="cf-aside cf-desk-only">
            {canFill ? (
              <div className="fm-card">
                {mainButton}
                <button type="button" className="fm-btn outline block" onClick={backToList}>
                  {t('appChecklists.later')}
                </button>
                <div className="fm-hint" style={{ textAlign: 'center' }}>
                  {t('appChecklists.keptHint')}
                </div>
              </div>
            ) : null}
            {deviations.length > 0 ? (
              <div className="fm-card" data-testid="checklist-deviations">
                <div className="cf-side-title">
                  <TriangleAlert aria-hidden width={20} height={20} />
                  {t('appChecklists.deviationsTitle')}{' '}
                  <span className="fm-h-count">{deviations.length}</span>
                </div>
                <div>
                  {deviations.map((state) => (
                    <div key={state.item.id} className="cf-devrow">
                      <TriangleAlert aria-hidden width={18} height={18} />
                      <div>
                        <div className="fm-title" style={{ fontSize: 15 }}>
                          {state.item.label}
                        </div>
                        <div className="fm-meta">
                          <ValueText state={state} withWaiting />
                        </div>
                        {state.comment ? (
                          <div className="cl-done-note">“{state.comment}”</div>
                        ) : null}
                      </div>
                    </div>
                  ))}
                </div>
                <div className="fm-hint">{t('appChecklists.handlerHint')}</div>
              </div>
            ) : null}
            <DoneBy states={states} />
          </aside>
        ) : null}
        {toast}
      </div>
    </DeskShell>
  );
}

/** Een getal zoals een mens het leest: "5,4" in het Nederlands, niet "5.4". */
function useNumber() {
  const { i18n } = useTranslation();
  return (value: string) => {
    const number = Number(value.replace(',', '.'));
    return Number.isFinite(number) ? new Intl.NumberFormat(i18n.language).format(number) : value;
  };
}

/** De norm in woorden: "tussen 8 en 12 °C", of `null` zonder grenzen. */
function useNorm() {
  const { t } = useTranslation();
  const number = useNumber();
  return (item: ChecklistItem): string | null => {
    const unit = item.unit;
    if (item.min !== null && item.max !== null) {
      return t('appChecklists.normBetween', {
        min: number(String(item.min)),
        max: number(String(item.max)),
        unit,
      }).trim();
    }
    if (item.min !== null) {
      return t('appChecklists.normAtLeast', { min: number(String(item.min)), unit }).trim();
    }
    if (item.max !== null) {
      return t('appChecklists.normAtMost', { max: number(String(item.max)), unit }).trim();
    }
    return null;
  };
}

/** Het antwoord in één regel: "Ja", "14 °C · norm tussen 8 en 12 °C", "2 foto's", “…”. */
function ValueText({ state, withWaiting = false }: { state: ItemState; withWaiting?: boolean }) {
  const { t } = useTranslation();
  const number = useNumber();
  const norm = useNorm();
  let text: string;
  switch (state.item.type) {
    case 'check':
      text = state.value === 'ja' ? t('appChecklists.yes') : t('appChecklists.no');
      break;
    case 'number': {
      const value = `${number(state.value)}${state.item.unit ? ` ${state.item.unit}` : ''}`;
      const normText = norm(state.item);
      text = normText ? t('appChecklists.valueNorm', { value, norm: normText }) : value;
      break;
    }
    case 'photo':
      text = t('appChecklists.photos', { count: state.fileIds.length });
      break;
    default:
      text = state.value ? `“${state.value}”` : '—';
  }
  if (withWaiting && state.queued) text = `${text} · ${t('appChecklists.waitingShort')}`;
  return <>{text}</>;
}

/** Een afgevinkt item: een compacte grijze rij. Een tik klapt hem open om hem aan te passen. */
function DoneRow({
  state,
  expandable,
  onOpen,
}: {
  state: ItemState;
  expandable: boolean;
  onOpen: () => void;
}) {
  const { t } = useTranslation();
  const clock = useClock();

  const content: ReactNode = (
    <>
      <span className="cl-done-icon">
        {state.queued ? (
          <Clock aria-hidden width={16} height={16} strokeWidth={2.5} />
        ) : state.deviation ? (
          <TriangleAlert aria-hidden width={16} height={16} strokeWidth={2.5} />
        ) : (
          <Check aria-hidden width={16} height={16} strokeWidth={3} />
        )}
      </span>
      <span className="cl-done-info">
        <span className="cl-done-label">{state.item.label}</span>
        {state.deviation || state.queued ? (
          <span className="cf-badges">
            {state.deviation ? (
              <FloorBadge
                tone="attention"
                icon={<TriangleAlert aria-hidden width={14} height={14} />}
              >
                {t('appChecklists.deviation')}
              </FloorBadge>
            ) : null}
            {state.queued ? (
              <FloorBadge icon={<Clock aria-hidden width={14} height={14} />}>
                {t('appChecklists.waiting')}
              </FloorBadge>
            ) : null}
          </span>
        ) : null}
        <span className="cl-done-value">
          <ValueText state={state} />
        </span>
        {state.comment && state.item.type !== 'text' ? (
          <span className="cl-done-note">“{state.comment}”</span>
        ) : null}
        {state.doneAt ? (
          <span className="cl-done-by">
            {t('appChecklists.doneBy', {
              name: state.doneByName ?? '?',
              when: clock(state.doneAt),
            })}
          </span>
        ) : null}
      </span>
      {expandable ? (
        <span className="cl-done-right">
          <ChevronRight aria-hidden width={22} height={22} className="fm-chev" />
        </span>
      ) : null}
    </>
  );

  const className = cn('cl-done', state.deviation && 'dev', state.queued && 'queued');
  return expandable ? (
    <button
      type="button"
      className={className}
      aria-expanded={false}
      onClick={onOpen}
      data-testid={`checklist-item-${state.item.id}`}
    >
      {content}
    </button>
  ) : (
    <div className={className} data-testid={`checklist-item-${state.item.id}`}>
      {content}
    </div>
  );
}

/**
 * Eén open item.
 *
 * Een ja/nee-item krijgt **twee knoppen van 64 px** en geen schakelaar: met handschoenen aan is een
 * schakelaar niet te raken, en "ja" en "nee" zijn hier geen instelling maar een antwoord.
 */
function ItemEditor({
  runId,
  state,
  disabled,
  online,
  current,
  onFocus,
  onSaved,
}: {
  runId: string;
  state: ItemState;
  disabled: boolean;
  online: boolean;
  current: boolean;
  onFocus: () => void;
  onSaved: () => void;
}) {
  const { t } = useTranslation();
  const { api } = useSession();
  const queryClient = useQueryClient();
  const norm = useNorm();
  const number = useNumber();
  const { item } = state;

  const [value, setValue] = useState(
    item.type === 'number' && state.value ? number(state.value) : state.value,
  );
  const [comment, setComment] = useState(state.comment);
  const [photos, setPhotos] = useState<Photo[]>(
    state.fileIds.map((fileId) => ({ fileId, previewUrl: null })),
  );
  const [error, setError] = useState<string | null>(null);

  const refresh = () => void queryClient.invalidateQueries({ queryKey: MY_RUNS });

  /**
   * Afvinken: eerst in de wachtrij, dan versturen.
   *
   * De wachtrij is niet alleen voor offline: ook online gaat het er eerst in, zodat een verzoek dat
   * halverwege afbreekt niet verloren gaat. `flushOutbox` stuurt hem meteen daarna.
   */
  const save = useMutation({
    // Offline niet laten pauzeren: de wachtrij ís het offline pad (testrapport #3).
    ...HANDLES_OFFLINE,
    mutationFn: async (next: { value: string }) => {
      setError(null);
      const key = await enqueue({
        kind: 'checklist-item',
        postId: runId,
        itemId: item.id,
        value: next.value,
        comment,
      });
      const result = await flushOutbox(api);
      // `flushOutbox` gooit nooit: een regel die de server weigerde, is stil weg. Dan is er niets
      // bewaard en mag het item niet dichtklappen alsof het gelukt is. Offline blijft de regel
      // gewoon wachten en is dit geen fout.
      if (result.geweigerd.includes(key)) throw new Error('Geweigerd door de server');
      return result;
    },
    onSuccess: (result) => {
      // Wat er met deze ronde mee verzonden werd (ook een oudere bevestiging), ververst zijn eigen
      // schermen; deze ronde zelf ververst altijd, ook offline, voor het klokje.
      for (const queryKey of queriesAfterSending(result.verzondenSoorten)) {
        void queryClient.invalidateQueries({ queryKey });
      }
      refresh();
      onSaved();
    },
    onError: (problem) => {
      // De geweigerde regel staat niet meer in de wachtrij; het scherm toont weer wat de server weet.
      refresh();
      setError(problemDetail(problem) ?? t('errors.generic'));
    },
  });

  /** Met foto's gaat het rechtstreeks: een bestand-id dat nog niet bestaat, kan niet wachten. */
  const saveWithPhotos = useMutation({
    mutationFn: async (ids: string[]) => {
      const { error: problem } = await api.PUT('/api/v1/me/checklists/{runId}/items/{itemId}', {
        params: { path: { runId, itemId: item.id } },
        body: { value: '', fileIds: ids, comment, clientOccurredAt: new Date().toISOString() },
      });
      if (problem) throw problem;
    },
    onSuccess: refresh,
    onError: (problem) => {
      const fieldErrors = (problem as { errors?: { message: string }[] } | undefined)?.errors;
      setError(fieldErrors?.[0]?.message ?? problemDetail(problem) ?? t('errors.generic'));
    },
  });

  const normText = norm(item);
  // Een afwijking vóór de badge: zodra de getypte waarde buiten de norm valt, nog vóór Opslaan.
  const typedDeviation = item.type === 'number' && localDeviation(item, value);
  const below = item.min !== null && Number(value.replace(',', '.')) < item.min;

  return (
    <section
      className={cn('cl-item', current && 'current')}
      aria-label={item.label}
      onFocusCapture={onFocus}
      data-testid={`checklist-item-${item.id}`}
    >
      <div className="cl-item-top">
        <div className="cl-item-label">
          {item.label}
          {item.required ? (
            <span className="cl-req" aria-hidden>
              *
            </span>
          ) : null}
          {item.help ? <div className="cl-help">{item.help}</div> : null}
        </div>
      </div>

      {item.type === 'check' ? (
        <div className="cl-yn">
          {(['ja', 'nee'] as const).map((answer) => (
            <button
              key={answer}
              type="button"
              className={cn(state.value === answer && 'on')}
              aria-pressed={state.value === answer}
              disabled={disabled || save.isPending}
              onClick={() => save.mutate({ value: answer })}
              data-testid={`item-${item.id}-${answer}`}
            >
              {answer === 'ja' ? (
                <Check aria-hidden width={22} height={22} strokeWidth={2.5} />
              ) : (
                <X aria-hidden width={22} height={22} strokeWidth={2.5} />
              )}
              {answer === 'ja' ? t('appChecklists.yes') : t('appChecklists.no')}
            </button>
          ))}
        </div>
      ) : item.type === 'number' ? (
        <>
          <div className="fm-field">
            <label className="fm-label" htmlFor={`item-${item.id}-value`}>
              {item.unit
                ? t('appChecklists.valueLabel', { unit: item.unit })
                : t('appChecklists.value')}
            </label>
            <div className="cl-numrow">
              <div className={cn('cl-numfield', typedDeviation && 'dev', disabled && 'disabled')}>
                <input
                  id={`item-${item.id}-value`}
                  inputMode="decimal"
                  value={value}
                  placeholder={t('appChecklists.value')}
                  disabled={disabled}
                  onChange={(event) => setValue(event.target.value)}
                  data-testid={`item-${item.id}-value`}
                />
                {item.unit ? <span className="cl-unit">{item.unit}</span> : null}
              </div>
              <button
                type="button"
                className={cn('fm-btn', (disabled || value.trim() === '') && 'is-disabled')}
                disabled={disabled || value.trim() === '' || save.isPending}
                onClick={() => save.mutate({ value })}
                data-testid={`item-${item.id}-save`}
              >
                {t('appChecklists.save')}
              </button>
            </div>
            {normText ? (
              <div className="cl-norm">
                <Info aria-hidden width={16} height={16} />
                {t('appChecklists.normLine', { norm: normText })}
              </div>
            ) : null}
          </div>
          {typedDeviation ? (
            <div className="cl-warn" role="alert" data-testid={`item-${item.id}-warning`}>
              <TriangleAlert aria-hidden width={20} height={20} />
              <div>
                <strong>
                  {t(below ? 'appChecklists.below' : 'appChecklists.above', {
                    value: `${value.trim()}${item.unit ? ` ${item.unit}` : ''}`,
                  })}
                </strong>{' '}
                {t('appChecklists.warnRest')}
              </div>
            </div>
          ) : null}
          <div className="fm-field">
            <label className="fm-label" htmlFor={`item-${item.id}-comment`}>
              {t('appChecklists.commentLabel')}{' '}
              <span className="fm-label-opt">{t('appChecklists.commentWhen')}</span>
            </label>
            <input
              id={`item-${item.id}-comment`}
              className={cn('fm-input', typedDeviation && 'attention')}
              value={comment}
              maxLength={500}
              disabled={disabled}
              placeholder={t('appChecklists.commentPlaceholder')}
              onChange={(event) => setComment(event.target.value)}
              data-testid={`item-${item.id}-comment`}
            />
          </div>
        </>
      ) : item.type === 'photo' ? (
        <>
          <PhotoPicker
            photos={photos}
            max={CHECKLIST_MAX_PHOTOS}
            onChange={(next) => {
              setPhotos(next);
              if (next.length > 0) saveWithPhotos.mutate(next.map((photo) => photo.fileId));
            }}
            ownerModule="tasks"
            purpose="checklist_photo"
            disabled={disabled}
            offline={!online}
            offlineHint={t('appChecklists.photoOffline')}
            testId={`item-${item.id}-photo`}
          />
          <div className="fm-hint">
            {t('appChecklists.maxPhotos', { count: CHECKLIST_MAX_PHOTOS })}
          </div>
        </>
      ) : (
        <>
          <textarea
            className="fm-input area"
            aria-label={item.label}
            value={value}
            disabled={disabled}
            maxLength={500}
            onChange={(event) => setValue(event.target.value)}
            data-testid={`item-${item.id}-text`}
          />
          <div className="ff-actions" style={{ justifyContent: 'space-between' }}>
            <span className="cf-count">{t('appChecklists.count500', { count: value.length })}</span>
            <button
              type="button"
              className={cn('fm-btn small', (disabled || value.trim() === '') && 'is-disabled')}
              disabled={disabled || value.trim() === '' || save.isPending}
              onClick={() => save.mutate({ value })}
              data-testid={`item-${item.id}-save-text`}
            >
              {t('appChecklists.save')}
            </button>
          </div>
        </>
      )}

      {error ? <FieldError>{error}</FieldError> : null}
    </section>
  );
}

/** Wie van de ploeg afvinkte: per persoon het aantal items en het laatste uur (spec §3). */
function DoneBy({ states }: { states: ItemState[] }) {
  const { t } = useTranslation();
  const { profile } = useSession();
  const clock = useClock();

  const people = useMemo(() => {
    const map = new Map<string, { name: string; count: number; last: string; waiting: number }>();
    for (const state of states) {
      if (!state.done || !state.doneAt) continue;
      const key = state.doneById ?? state.doneByName ?? '?';
      const entry = map.get(key) ?? {
        name: state.doneByName ?? '?',
        count: 0,
        last: state.doneAt,
        waiting: 0,
      };
      entry.count += 1;
      if (state.queued) entry.waiting += 1;
      if (state.doneAt > entry.last) entry.last = state.doneAt;
      map.set(key, entry);
    }
    return [...map.entries()];
  }, [states]);

  if (people.length === 0) return null;

  return (
    <div className="fm-card" data-testid="checklist-done-by">
      <div className="cf-side-title">
        <Users aria-hidden width={20} height={20} />
        {t('appChecklists.doneByTitle')}
      </div>
      <div>
        {people.map(([key, person]) => (
          <div key={key} className="cf-person">
            <Avatar name={person.name} self={key === profile?.personId} />
            <div className="cf-person-info">
              <div className="fm-title" style={{ fontSize: 15 }}>
                {key === profile?.personId
                  ? t('appChecklists.you', { name: person.name })
                  : person.name}
              </div>
              <div className="fm-meta">
                {person.waiting === person.count
                  ? t('appChecklists.personWaiting', { count: person.count })
                  : t('appChecklists.personItems', {
                      count: person.count,
                      time: clock(person.last),
                    })}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * De kaart op het beginscherm.
 *
 * Verdwijnt als er niets klaarstaat: geen recht, module uit of niets te doen geeft allemaal "geen
 * kaart". Zelfde keuze als bij de formulieren en de meldknop — geen zesde tab.
 */
export function AppChecklistsCard() {
  const { t } = useTranslation();
  const { api } = useSession();

  const runs = useQuery({
    queryKey: MY_RUNS,
    queryFn: async (): Promise<ChecklistRun[]> => {
      const { data, error, response } = await api.GET('/api/v1/me/checklists', {});
      if (error || !data) return absentOrThrow(response, error);
      return (data as ChecklistRunList).items;
    },
  });

  const items = runs.data ?? [];
  if (items.length === 0) return null;

  const overdue = items.filter((run) => run.status === 'overdue').length;

  return (
    <Card compact data-app-card>
      <Link to="/app/checklists" className="flex min-h-12 items-center gap-element">
        <span aria-hidden className="rounded-pill bg-brand-primary-tint p-2 text-brand-primary">
          <ClipboardCheck className="h-5 w-5" />
        </span>
        <span className="min-w-0 flex-1">
          <CardTitle>{t('appChecklists.title')}</CardTitle>
          <span className="block text-small text-text-secondary">
            {t('appChecklists.cardHint', { count: items.length })}
          </span>
        </span>
        {overdue > 0 ? (
          <Badge tone="danger">{t('appChecklists.overdueCount', { count: overdue })}</Badge>
        ) : null}
      </Link>
    </Card>
  );
}
